import {inject, Injectable} from '@angular/core';
import {ApiCallService} from "./api-call.service";
import {Session} from "./session.service";
import {BehaviorSubject, Observable} from "rxjs";
import {finalize, map, tap} from "rxjs/operators";
import {BlockUIService} from "ng-block-ui";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authObservable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private apiCall = inject(ApiCallService);
  private session = inject(Session);
  private blockUI = inject(BlockUIService);

  constructor() {
    if(this.isAuthenticated()){
      this.setAuthObservable(true);
    }
  }

  isAuthenticated() {
    return !!this.session.getUser();
  }

  getAuthObservable(){
    return this.authObservable;
  }

  setAuthObservable(flag: boolean){
    this.authObservable.next(flag);
  }

  loadSession(userData: any) {
    this.session.create(userData);
    this.authObservable.next(true);
    return true;
  }

  reloadSession(): Observable<boolean> {
    this.session.destroy();
    this.blockUI.start('main');

    return this.apiCall.getData('/getusersession/', {}).pipe(
      // Use map to transform the incoming response into a boolean
      map(response => response?.success && response.data?.[0]?.dataSet),

      // Use tap for side effects (actions that don't modify the stream)
      tap(userData => {
        if (userData) {
          this.session.create(userData);
          this.authObservable.next(true);
        }
      }),

      // Convert the final stream value (userData or falsy) to a boolean
      map(userData => !!userData),

      // Use finalize to guarantee this code runs on success OR error
      finalize(() => {
        this.blockUI.stop('main');
      })
    );
  }

  checkRules(checks: PasswordChecks, password:string, confirmPassword: string) {
    let errorMessage: string[] = [];
    checks.passHasErrors = false;
    if( password.length < 12 ) {
      errorMessage.push( "The password length must be a minimum of twelve (12) characters." );
      checks.passHasErrors = true;
      checks.passLengthOK = false;
    } else {
      checks.passLengthOK = true;
    }
    if( !/[!@#$%^&*(),.?":{}|<>]/.test( password ) ) {
      errorMessage.push( "The password must include at least one Special Character." );
      checks.passHasErrors = true;
      checks.passSpecialCharsOK = false;
    } else {
      checks.passSpecialCharsOK = true;
    }
    if( !/[A-Z]/.test( password ) ) {
      errorMessage.push( "The password must include at least one Upper case letter." );
      checks.passHasErrors = true;
      checks.passUpperOK = false;
    } else {
      checks.passUpperOK = true;
    }
    if( !/[a-z]/.test( password ) ) {
      errorMessage.push( "The password must include at least one Lower case letter." );
      checks.passHasErrors = true;
      checks.passLowerOK = false;
    } else {
      checks.passLowerOK = true;
    }
    if( !/\d/.test( password ) ) {
      errorMessage.push( "The password must include at least one Number." );
      checks.passHasErrors = true;
      checks.passNumberOK = false;
    } else {
      checks.passNumberOK = true;
    }

    if(confirmPassword !== password) {
      checks.passHasErrors = true;
      checks.passConfirmOK = false;
    } else {
      checks.passConfirmOK = true;
    }
  }
}


export interface PasswordChecks {
  errorMessage: string,
  startedCheck: boolean,
  startedConfirmCheck: boolean,
  passHasErrors: boolean,
  passLengthOK: boolean,
  passSpecialCharsOK: boolean,
  passUpperOK: boolean,
  passNumberOK: boolean,
  passLowerOK: boolean,
  passConfirmOK: boolean
}
